import { useEffect, useState } from 'react';
import { useKeyboard } from './useKeyboard';
import type { ShiftSummary } from '@dastarhan/contracts';
import { formatMoney, moneyInWords, zReportLines } from '@dastarhan/core';
import { api } from '../api';
import { isVirtualPrinter, paperWidth, printSmart } from '../print';

export function ShiftClose({ token, shiftId, queued, onConfirm, onBack }: {
  token: string;
  shiftId: string;
  queued: number;
  onConfirm: (closingCash: number) => void;
  onBack: () => void;
}) {
  // Сколько броней на завтра — знание для утренней смены
  const [tomorrow, setTomorrow] = useState<number | null>(null);
  useEffect(() => {
    const d = new Date(Date.now() + 86400_000).toISOString().slice(0, 10);
    void api.reservationsOn(token, d)
      .then((r) => setTomorrow(r.rows.filter((x) => x.status !== 'CANCELLED').length))
      .catch(() => setTomorrow(null));
  }, [token]);

  const [sum, setSum] = useState<ShiftSummary | null>(null);
  const [offline, setOffline] = useState(false);
  const [cash, setCash] = useState('');
  // Суммы на планшете набирают своей клавиатурой: системная
  // либо не всплывает, либо закрывает половину экрана
  const kb = useKeyboard(cash, setCash, 'number');

  useEffect(() => {
    api.shiftSummary(token, shiftId)
      .then(setSum)
      .catch(() => setOffline(true));
  }, [token, shiftId]);

  const closing = Math.round(Number(cash.replace(',', '.')) * 100) || 0;
  const diff = sum ? closing - sum.expectedCash : 0;

  return (
    <div className="pay">
      <button className="btn ghost" style={{ alignSelf: 'flex-start' }} onClick={onBack}>← К заказу</button>
      <h1>Закрытие смены{sum ? <> — {formatMoney(sum.cashSales + sum.cardSales + sum.qrSales)}</> : null}</h1>
      {sum && <p className="hint">Выручка смены прописью: {moneyInWords(sum.cashSales + sum.cardSales + sum.qrSales)}.</p>}

      {sum && (
        <div className="methods-summary">
          <div><span>Чеков</span><b>{sum.checks}</b></div>
          <div><span>Наличные</span><b className="money">{formatMoney(sum.cashSales)}</b></div>
          <div><span>Карта</span><b className="money">{formatMoney(sum.cardSales)}</b></div>
          <div><span>Kaspi QR</span><b className="money">{formatMoney(sum.qrSales)}</b></div>
        </div>
      )}
      {offline && (
        <p className="hint">Сводка недоступна без связи. Смену можно закрыть — отчёт досчитается на сервере, когда касса выйдет в сеть.</p>
      )}
      {queued > 0 && (
        <p className="hint">В очереди {queued} неотправленных чеков — они уйдут вместе с закрытием.</p>
      )}

      {sum && (
        <>
          <div className="due-board">
            <span>Должно быть в ящике</span>
            <span className="money">{formatMoney(sum.expectedCash)}</span>
          </div>
          {(sum.topDishes?.length ?? 0) > 0 && (
            <p className="hint" style={{ margin: '6px 0 0' }}>
              Хиты смены: {sum.topDishes!.map((d) => `${d.name} ×${d.qty}`).join(', ')}
            </p>
          )}
        </>
      )}

      {tomorrow !== null && tomorrow > 0 && (
        <p className="hint">📅 Завтра броней: {tomorrow} — подготовьте зал заранее</p>
      )}
      {(() => {
        // усталость к концу длинной смены — повод пересчитать ящик внимательнее
        const at = localStorage.getItem('dastarhan.shiftOpenedAt');
        if (!at) return null;
        const h = Math.floor((Date.now() - new Date(at).getTime()) / 3600_000);
        if (h < 1) return null;
        return (
          <p className="hint">Смена идёт {h} ч{h >= 12 ? ' — сверьте ящик особенно внимательно' : ''}</p>
        );
      })()}
      <label className="cash-input">
        Сколько наличных в ящике фактически, ₸
        <input inputMode="decimal" className="field" placeholder="0"
          {...kb.props}
          value={cash} onChange={(e) => setCash(e.target.value)} />
        {cash !== '' && closing > 0 && (
          <small className="hint-inline">{moneyInWords(closing)}</small>
        )}
      </label>
      {/* Экранная клавиатура: на планшете системной может не быть вовсе —
          без этой строки поле немое (урок Шахзода, 08.08) */}
      {kb.node}

      {sum && cash !== '' && (
        <div className="change-row">
          <span>{diff === 0 ? 'Сходится' : diff > 0 ? 'Излишек' : 'Недостача'}</span>
          <span className="money" style={diff !== 0 ? { color: 'var(--danger)' } : undefined}>
            {formatMoney(Math.abs(diff))}
          </span>
        </div>
      )}

      {sum && (
        <button className="btn" onClick={() => void (async () => {
          /*
           * Z-отчёт по канону: полный агрегат сервера с пересчётом,
           * номером смены и подписями. Нет связи — прежний краткий
           * лист: цифры дня всё равно уезжают на бумагу
           */
          try {
            const r = await api.shiftReport(token, shiftId!);
            const L = zReportLines(r,
              localStorage.getItem('dastarhan.place') ?? 'Dastarhan',
              { actualCash: cash === '' ? r.expectedCash : closing },
              paperWidth());
            if (await isVirtualPrinter()) {
              // виртуальному принтеру сырьё не по зубам — лента на экран
              window.dispatchEvent(new CustomEvent('dastarhan:tape', { detail: L }));
            } else await printSmart(L);
            return;
          } catch { /* офлайн — краткий лист ниже */ }
          const L: string[] = [
            '*** ЗАКРЫТИЕ СМЕНЫ ***',
            new Date().toLocaleString('ru-RU'),
            '────────────────',
            `Чеков: ${sum.checks}`,
            `Наличные: ${formatMoney(sum.cashSales)}`,
            `Карта: ${formatMoney(sum.cardSales)}`,
            `Kaspi QR: ${formatMoney(sum.qrSales)}`,
            `Ожидается в ящике: ${formatMoney(sum.expectedCash)}`,
          ];
          if (cash !== '') {
            L.push(`Фактически: ${formatMoney(closing)}`,
              diff === 0 ? 'Сходится ✓'
                : `${diff > 0 ? 'Излишек' : 'Недостача'}: ${formatMoney(Math.abs(diff))}`);
          }
          if ((sum.tipsCard ?? 0) > 0) {
            L.push(`На чай картой: ${formatMoney(sum.tipsCard!)} (официантам)`);
          }
          if ((sum.topDishes?.length ?? 0) > 0) {
            L.push('────────────────', 'Хиты смены:');
            for (const d of sum.topDishes!) L.push(`  ${d.name} ×${d.qty}`);
          }
          void printSmart(L);
        })()}>🖨 Z-отчёт (лист закрытия)</button>
      )}

      <button className="btn accent" disabled={cash === ''}
        onClick={() => {
          // смену закрывают один раз за день — сумма проговаривается вслух
          const diffText = !sum ? ''
            : diff === 0 ? ' Расхождений нет.'
              : diff > 0 ? ` Излишек ${formatMoney(diff)}.`
                : ` Недостача ${formatMoney(-diff)}.`;
          if (!window.confirm(
            `В ящике ${formatMoney(closing)} — ${moneyInWords(closing)}.${diffText}\nЗакрыть смену?`,
          )) return;
          onConfirm(closing);
        }}>
        Закрыть смену
      </button>
      {kb.node}
    </div>
  );
}
